import { useKeyboard } from "@react-native-community/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Text, TextInput } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import DialogWrapper from "../../DialogWrapper";

type Props = {
	visible: boolean;
	onClose: () => void;
	initialFirstName: string;
	initialLastName: string;
	onSave: (names: { firstName: string; lastName: string }) => void;
	isSaving: boolean;
	error?: string;
};

export const EditNameDialog = ({
	visible,
	onClose,
	initialFirstName,
	initialLastName,
	onSave,
	isSaving,
	error,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const { keyboardHeight } = useKeyboard();
	const [draftFirstName, setDraftFirstName] = useState(initialFirstName);
	const [draftLastName, setDraftLastName] = useState(initialLastName);

	useEffect(() => {
		if (visible) {
			setDraftFirstName(initialFirstName);
			setDraftLastName(initialLastName);
		}
	}, [visible, initialFirstName, initialLastName]);

	const trimmedFirstName = draftFirstName.trim();
	const trimmedLastName = draftLastName.trim();
	const hasChanges =
		trimmedFirstName !== initialFirstName.trim() ||
		trimmedLastName !== initialLastName.trim();
	const canSave = hasChanges && !isSaving;

	const handleSave = () => {
		onSave({ firstName: trimmedFirstName, lastName: trimmedLastName });
	};

	return (
		<DialogWrapper
			visible={visible}
			handleClose={onClose}
			maxWidth="xs"
			bottom={keyboardHeight ? keyboardHeight - 200 : 0}
		>
			<Dialog.Title>{t("account.editName.title")}</Dialog.Title>
			<Dialog.Content style={{ gap: 16 }}>
				<Box flexDirection={isMobile ? "column" : "row"} gap={12}>
					<TextInput
						mode="outlined"
						label={t("onboarding.steps.profile.firstNameLabel")}
						value={draftFirstName}
						onChangeText={setDraftFirstName}
						autoCapitalize="words"
						autoCorrect={false}
						autoComplete="given-name"
						textContentType="givenName"
						returnKeyType="next"
						autoFocus
						error={Boolean(error)}
						style={{ flexGrow: 1, backgroundColor: theme.colors.surface }}
					/>
					<TextInput
						mode="outlined"
						label={t("onboarding.steps.profile.lastNameLabel")}
						value={draftLastName}
						onChangeText={setDraftLastName}
						autoCapitalize="words"
						autoCorrect={false}
						autoComplete="family-name"
						textContentType="familyName"
						returnKeyType="done"
						error={Boolean(error)}
						style={{ flexGrow: 1, backgroundColor: theme.colors.surface }}
					/>
				</Box>
				{error ? (
					<Text
						variant="bodySmall"
						style={{
							color: theme.colors.error,
							paddingHorizontal: 16,
						}}
					>
						{error}
					</Text>
				) : null}
			</Dialog.Content>
			<Dialog.Actions>
				<Button onPress={onClose} disabled={isSaving}>
					{t("common.cancel")}
				</Button>
				<Button
					mode="contained"
					onPress={handleSave}
					disabled={!canSave}
					loading={isSaving}
					style={{ minWidth: 100 }}
				>
					{t("account.save")}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};
